import React, { useState, useEffect, useMemo } from "react";
import { getCourses, getConfig } from "../../api/service";
import { capitalizeFirstLetter } from "../../utils/letters";
import {
	commitRutField,
	INVALID_RUT_MESSAGE,
	RUT_DIGITS_HINT,
	sanitizeRutDigitsOnly,
	validateRut,
} from "../../utils/rutUtils";
import { useNavigate } from "react-router-dom";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;


export default function AdmissionPage() {
	const navigate = useNavigate();
	const [courses, setCourses] = useState([]);
	const [loadingCourses, setLoadingCourses] = useState(true);
	const [rut, setRut] = useState("");
	const [razonSocial, setRazonSocial] = useState("");
	const [correo, setCorreo] = useState("");
	const [selectedCourses, setSelectedCourses] = useState([]);
	const [cantidadEstudiantes, setCantidadEstudiantes] = useState("");
	const [rutError, setRutError] = useState("");
	const [fieldErrors, setFieldErrors] = useState({});
	const [whatsappNumber, setWhatsappNumber] = useState("56912345678");
	const MAX_COURSES = 3;
	const DEFAULT_MAX_STUDENTS = 20;


	useEffect(() => {
		let cancelled = false;

		getConfig()
			.then((data) => {
				if (!cancelled && data) {
					const config = Array.isArray(data) ? data[0] : data;
					if (config.contacto_telefono) setWhatsappNumber(config.contacto_telefono);
				}
			})
			.catch((err) => console.error("Error cargando configuración", err));

		getCourses()
			.then((data) => {
				if (!cancelled) setCourses(Array.isArray(data) ? data : []);
			})
			.catch(() => {
				if (!cancelled) setCourses([]);
			})
			.finally(() => {
				if (!cancelled) setLoadingCourses(false);
			});
		return () => {
			cancelled = true;
		};
	}, []);

	// Cupos máximos según los cursos seleccionados (mínimo de cupos disponibles de cada curso)
	const maxEstudiantes = useMemo(() => {
		if (selectedCourses.length === 0) return DEFAULT_MAX_STUDENTS;
		const cupos = selectedCourses.map((c) => {
			const cupo =
				c.cupos_disponibles ?? c.remaining_slots ?? c.max_students;
			return typeof cupo === "number" && cupo >= 0
				? cupo
				: DEFAULT_MAX_STUDENTS;
		});
		const min = Math.min(...cupos);
		return min > 0 ? min : DEFAULT_MAX_STUDENTS;
	}, [selectedCourses]);

	const handleRutChange = (e) => {
		setRut(sanitizeRutDigitsOnly(e.target.value));
		if (fieldErrors.rut) setFieldErrors((p) => ({ ...p, rut: undefined }));
		if (rutError) setRutError("");
	};

	const handleRutBlur = () => {
		if (!rut.trim()) {
			setRutError("");
			return;
		}
		const r = commitRutField(rut);
		if (r.ok) {
			setRut(r.formatted);
			setRutError("");
		} else {
			setRutError(r.message || INVALID_RUT_MESSAGE);
		}
	};

	const handleCourseChange = (e) => {
		const option = e.target.options[e.target.selectedIndex];
		if (!option || !option.value) return;
		const id = Number(option.value);
		const course = courses.find((c) => c.id === id);
		const name =
			course?.name || course?.code || option.text || `Curso ${id}`;
		setSelectedCourses((prev) => {
			if (prev.some((c) => c.id === id)) return prev;
			if (prev.length >= MAX_COURSES) return prev;
			return [...prev, { id, name, ...course }];
		});
	};

	const removeCourse = (id) => {
		setSelectedCourses((prev) => prev.filter((c) => c.id !== id));
	};

	const buildMessage = () => {
		const cursosText =
			selectedCourses.length > 0
				? selectedCourses.map((c) => c.name).join(", ")
				: "—";
		const num = cantidadEstudiantes.trim() || "—";
		return `¡Hola! Estoy interesado en los cursos: ${capitalizeFirstLetter(cursosText)}, para ${num} estudiantes.\nRUT: ${rut || "—"}\nRazón social: ${razonSocial || "—"}\nCorreo: ${correo || "—"}.`;
	};

	const whatsAppUrl = () => {
		const text = encodeURIComponent(buildMessage());
		return `https://wa.me/${whatsappNumber.replace(/\D/g, "")}?text=${text}`;
	};

	const rutValidation = validateRut(rut);
	const correoTrimmed = correo.trim();
	const correoValid = EMAIL_REGEX.test(correoTrimmed);
	const razonSocialTrimmed = razonSocial.trim();
	const formValid =
		rutValidation.valid &&
		razonSocialTrimmed &&
		correoTrimmed &&
		correoValid &&
		selectedCourses.length > 0 &&
		cantidadEstudiantes.trim();
	const cantidadNum = parseInt(cantidadEstudiantes, 10);
	const cantidadValid =
		!isNaN(cantidadNum) &&
		cantidadNum >= 1 &&
		cantidadNum <= maxEstudiantes;
	const canSend = formValid && cantidadValid;

	const [isSending, setIsSending] = useState(false);
	const [sendSuccess, setSendSuccess] = useState(false);

	function validateAndShowErrors() {
		const err = {};
		if (!rut.trim()) err.rut = "El RUT es obligatorio.";
		else if (!rutValidation.valid) err.rut = INVALID_RUT_MESSAGE;
		if (!razonSocial.trim())
			err.razonSocial = "La razón social es obligatoria.";
		if (!correo.trim())
			err.correo = "El correo electrónico es obligatorio.";
		else if (!correoValid)
			err.correo = "El correo electrónico no tiene un formato válido.";
		if (selectedCourses.length === 0)
			err.cursos = "Debes seleccionar al menos un curso.";
		if (!cantidadEstudiantes.trim())
			err.cantidad = "La cantidad de estudiantes es obligatoria.";
		else if (!cantidadValid)
			err.cantidad = `Debe ser entre 1 y ${maxEstudiantes} (según cupos de los cursos seleccionados).`;
		setFieldErrors(err);
		return Object.keys(err).length === 0;
	}

	async function handleSendClick(e) {
		e.preventDefault();
		if (!canSend) {
			validateAndShowErrors();
			return;
		}

		setIsSending(true);
		try {
			const { publicApi } = await import("../../api/http");
			const cursosText = selectedCourses.map(c => c.name).join(", ");
			const cantidad = parseInt(cantidadEstudiantes, 10);
			await publicApi.post("/api/public/admision/", {
				rut: rut.trim(),
				razonSocial: razonSocialTrimmed,
				correo: correoTrimmed.toLowerCase(),
				cursos: cursosText,
				cantidadEstudiantes: cantidad,
			});
			setSendSuccess(true);
		} catch (err) {
			console.error("Error enviando correo de admisión", err);
			alert("Ocurrió un error al enviar la solicitud. Por favor intenta de nuevo.");
		} finally {
			setIsSending(false);
		}
	}

	return (
		<main className="admission-page font-n">
			<div className="btn-back-container">
				<button onClick={() => navigate(-1)}>
					<i className="ri-arrow-go-back-line d-inline d-md-none"></i>
					<span className="d-none d-md-inline">Volver</span>
				</button>
			</div>
			<section className="admission-sec">
				<div className="admission-block">
					<h1 className="admission-title">Admisión</h1>
					<p className="admission-subtitle">
						Completa los datos para solicitar admisión a los cursos.
					</p>

					<form
						className="admission-form"
						onSubmit={(e) => e.preventDefault()}
					>
						<div className="admission-row">
							<div className="admission-col">
								<div className="admission-field">
									<label htmlFor="admission-rut">
										RUT{" "}
										<span className="admission-required">
											*
										</span>
									</label>
									<input
										id="admission-rut"
										type="text"
										className={`admission-input ${rutError || fieldErrors.rut ? "admission-input--error" : ""}`}
										placeholder="Ej: 123456789"
										title={RUT_DIGITS_HINT}
										value={rut}
										onChange={handleRutChange}
										onBlur={handleRutBlur}
										onKeyDown={(e) => {
											if (e.key === "Enter") {
												e.preventDefault();
												e.currentTarget.blur();
											}
										}}
										maxLength={10}
										aria-invalid={
											!!(rutError || fieldErrors.rut)
										}
										aria-describedby={
											rutError || fieldErrors.rut
												? "admission-rut-error"
												: undefined
										}
									/>
									<p className="admission-field-hint small text-muted mb-0">
										{RUT_DIGITS_HINT}
									</p>
									{(rutError || fieldErrors.rut) && (
										<p
											id="admission-rut-error"
											className="admission-field-error"
											role="alert"
										>
											{rutError || fieldErrors.rut}
										</p>
									)}
								</div>
								<div className="admission-field">
									<label htmlFor="admission-razon">
										Razón social{" "}
										<span className="admission-required">
											*
										</span>
									</label>
									<input
										id="admission-razon"
										type="text"
										className={`admission-input ${fieldErrors.razonSocial ? "admission-input--error" : ""}`}
										placeholder="Nombre de la organización"
										value={razonSocial}
										onChange={(e) => {
											setRazonSocial(e.target.value);
											if (fieldErrors.razonSocial)
												setFieldErrors((p) => ({
													...p,
													razonSocial: undefined,
												}));
										}}
										aria-invalid={!!fieldErrors.razonSocial}
									/>
									{fieldErrors.razonSocial && (
										<p
											className="admission-field-error"
											role="alert"
										>
											{fieldErrors.razonSocial}
										</p>
									)}
								</div>
								<div className="admission-field">
									<label htmlFor="admission-correo">
										Correo electrónico{" "}
										<span className="admission-required">
											*
										</span>
									</label>
									<input
										id="admission-correo"
										type="email"
										className={`admission-input ${fieldErrors.correo ? "admission-input--error" : ""}`}
										placeholder="correo@organizacion.com"
										value={correo}
										onChange={(e) => {
											setCorreo(e.target.value);
											if (fieldErrors.correo)
												setFieldErrors((p) => ({
													...p,
													correo: undefined,
												}));
										}}
										aria-invalid={!!fieldErrors.correo}
									/>
									{fieldErrors.correo && (
										<p
											className="admission-field-error"
											role="alert"
										>
											{fieldErrors.correo}
										</p>
									)}
								</div>
							</div>
							<div className="admission-col">
								<div className="admission-field">
									<label htmlFor="admission-cursos">
										Curso(s) de interés (máximo{" "}
										{MAX_COURSES}){" "}
										<span className="admission-required">
											*
										</span>
									</label>
									<select
										id="admission-cursos"
										className={`admission-select ${fieldErrors.cursos ? "admission-input--error" : ""}`}
										value=""
										onChange={(e) => {
											handleCourseChange(e);
											if (fieldErrors.cursos)
												setFieldErrors((p) => ({
													...p,
													cursos: undefined,
												}));
										}}
										disabled={
											loadingCourses ||
											selectedCourses.length >=
											MAX_COURSES
										}
										aria-invalid={!!fieldErrors.cursos}
									>
										<option value="">
											Seleccione un curso
										</option>
										{courses
											.filter(
												(c) =>
													!selectedCourses.some(
														(s) => s.id === c.id,
													),
											)
											.map((c) => (
												<option key={c.id} value={c.id}>
													{capitalizeFirstLetter(c.name) ||
														c.code ||
														`Curso ${c.id}`}
												</option>
											))}
									</select>
									{selectedCourses.length > 0 && (
										<ul className="admission-selected-courses">
											{selectedCourses.map((c) => (
												<li key={c.id}>
													<span>{capitalizeFirstLetter(c.name)}</span>
													<button
														type="button"
														className="admission-remove-course"
														onClick={() => {
															removeCourse(c.id);
															if (
																fieldErrors.cursos
															)
																setFieldErrors(
																	(p) => ({
																		...p,
																		cursos: undefined,
																	}),
																);
														}}
														aria-label="Quitar curso"
													>
														×
													</button>
												</li>
											))}
										</ul>
									)}
									{fieldErrors.cursos && (
										<p
											className="admission-field-error"
											role="alert"
										>
											{fieldErrors.cursos}
										</p>
									)}
								</div>
								<div className="admission-field">
									<label htmlFor="admission-cantidad">
										Cantidad de estudiantes (máximo{" "}
										{maxEstudiantes}){" "}
										<span className="admission-required">
											*
										</span>
									</label>
									<input
										id="admission-cantidad"
										type="number"
										min={1}
										max={maxEstudiantes}
										className={`admission-input ${fieldErrors.cantidad ? "admission-input--error" : ""}`}
										placeholder={`Máximo ${maxEstudiantes}`}
										value={cantidadEstudiantes}
										onChange={(e) => {
											setCantidadEstudiantes(
												e.target.value,
											);
											if (fieldErrors.cantidad)
												setFieldErrors((p) => ({
													...p,
													cantidad: undefined,
												}));
										}}
										aria-invalid={!!fieldErrors.cantidad}
									/>
									{(fieldErrors.cantidad ||
										(cantidadEstudiantes.trim() &&
											!cantidadValid)) && (
											<p
												className="admission-field-error"
												role="alert"
											>
												{fieldErrors.cantidad ||
													`Debe ser entre 1 y ${maxEstudiantes} (según cupos de los cursos seleccionados).`}
											</p>
										)}
								</div>
							</div>
						</div>

						<div className="admission-actions">
							{sendSuccess ? (
								<div className="alert alert-success text-center w-100">
									¡Tu solicitud ha sido enviada exitosamente! Nos pondremos en contacto contigo a la brevedad.
								</div>
							) : (
								<>
									<div className="admission-buttons-row">
										<a
											href={canSend ? whatsAppUrl() : "#"}
											className={`admission-btn admission-btn-wsp ${!canSend ? "admission-btn--disabled" : ""}`}
											target="_blank"
											rel="noreferrer"
											onClick={(e) => {
												if (!canSend) {
													e.preventDefault();
													validateAndShowErrors();
												}
											}}
											aria-disabled={!canSend}
										>
											<i
												className="ri-whatsapp-line d-inline"
												aria-hidden
											/>
											<span className="d-none d-md-inline">
												Enviar por WhatsApp
											</span>
										</a>
										<button
											type="button"
											className={`admission-btn admission-btn-email ${!canSend || isSending ? "admission-btn--disabled" : ""}`}
											onClick={handleSendClick}
											disabled={!canSend || isSending}
										>
											<i
												className="ri-mail-send-line d-inline"
												aria-hidden
											/>
											<span className="d-none d-md-inline">
												{isSending ? "Enviando..." : "Enviar solicitud"}
											</span>
										</button>
									</div>
									<p className="admission-hint">
										Puedes enviar tu solicitud por WhatsApp a Somnus
										o mediante nuestro formulario web. Completa todos
										los campos obligatorios y respeta el máximo de
										estudiantes según cupos disponibles.
									</p>
								</>
							)}
						</div>
					</form>
				</div>
			</section>
		</main>
	);
}
